import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { contasReceberService } from "../services/contasReceberService";
import { toast } from "sonner";
import { getApiErrorMessage } from "@/lib/apiClient";
import type { ContaReceber, BaixarContaReceberRequest, BaixarLoteRequest, FiltroContasReceber, TotaisFinanceiros,
  AtualizarContaReceberRequest, BaixarParcelaReceberRequest, BaixarParcelasLoteRequest,
  FiltroGerencialReceber } from "../types";

const TOTAIS_ZERADO: TotaisFinanceiros = {
  totalEmAberto: 0, totalQuitado: 0, totalAtrasado: 0, totalGeral: 0,
  qtdTotal: 0, qtdEmAberto: 0, qtdQuitado: 0, qtdAtrasado: 0,
  proximoVencimento: null, proximoVencimentoValor: null,
};

export const useContasReceber = (filtro: FiltroContasReceber = {}) => {
  const queryClient = useQueryClient();

  const { data: paginado, isLoading } = useQuery({
    queryKey: ["contas-receber", filtro],
    queryFn: () => contasReceberService.getAll(filtro),
    placeholderData: (prev) => prev,
  });

  const createMutation = useMutation({
    mutationFn: contasReceberService.create,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas-receber"] });
      toast.success("Conta a receber criada com sucesso");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarContaReceberRequest }) =>
      contasReceberService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas-receber"] });
      toast.success("Conta a receber atualizada com sucesso");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: contasReceberService.delete,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas-receber"] });
      toast.success("Conta a receber excluída com sucesso");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const baixarMutation = useMutation({
    mutationFn: ({ id, req }: { id: number; req: BaixarContaReceberRequest }) =>
      contasReceberService.baixar(id, req),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas-receber"] });
      toast.success("Recebimento registrado com sucesso");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const baixarLoteMutation = useMutation({
    mutationFn: (req: BaixarLoteRequest) => contasReceberService.baixarLote(req),
    onSuccess: (resultados) => {
      queryClient.invalidateQueries({ queryKey: ["contas-receber"] });
      const recebidas = resultados.filter((r) => r.isPago).length;
      toast.success(`${recebidas} conta(s) recebida(s) com sucesso`);
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  const estornarMutation = useMutation({
    mutationFn: (id: number) => contasReceberService.estornar(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contas-receber"] });
      toast.success("Estorno de recebimento realizado com sucesso");
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error));
    },
  });

  return {
    contas: paginado?.itens ?? [],
    total: paginado?.total ?? 0,
    totalPaginas: paginado?.totalPaginas ?? 0,
    totais: paginado?.totais ?? TOTAIS_ZERADO,
    isLoading,
    createConta: createMutation.mutate,
    updateConta: updateMutation.mutate,
    deleteConta: deleteMutation.mutate,
    baixarConta: baixarMutation.mutate,
    isBaixando: baixarMutation.isPending,
    baixarLote: baixarLoteMutation.mutate,
    isBaixandoLote: baixarLoteMutation.isPending,
    estornarConta: estornarMutation.mutate,
    isEstornando: estornarMutation.isPending,
  };
};

/**
 * Tela operacional de Contas a Receber: lista e opera POR PARCELA.
 *
 * O `useContasReceber` acima continua servindo a visão por TÍTULO (usada pelo
 * drawer de detalhe e por telas gerenciais). São granularidades diferentes de
 * propósito — misturá-las é o que produzia "quitei uma parcela e sumiu tudo".
 *
 * Toda mutação invalida as DUAS chaves, senão a grid por parcela e os KPIs por
 * título se desencontram depois de uma baixa.
 */
export const useParcelasReceber = (filtro: FiltroContasReceber = {}) => {
  const queryClient = useQueryClient();

  const invalidar = () => {
    queryClient.invalidateQueries({ queryKey: ["contas-receber-parcelas"] });
    queryClient.invalidateQueries({ queryKey: ["contas-receber"] });
  };

  const { data: paginado, isLoading } = useQuery({
    queryKey: ["contas-receber-parcelas", filtro],
    queryFn: () => contasReceberService.getParcelas(filtro),
    placeholderData: (prev) => prev,
  });

  const criarMutation = useMutation({
    mutationFn: contasReceberService.create,
    onSuccess: (conta) => {
      invalidar();
      const qtd = conta?.analiticas?.length ?? 0;
      toast.success(
        qtd > 1
          ? `Conta a receber lançada em ${qtd} parcelas`
          : "Conta a receber lançada com sucesso",
      );
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const atualizarMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: AtualizarContaReceberRequest }) =>
      contasReceberService.update(id, data),
    onSuccess: () => {
      invalidar();
      toast.success("Conta a receber atualizada");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const excluirTituloMutation = useMutation({
    mutationFn: (tituloId: number) => contasReceberService.delete(tituloId),
    onSuccess: () => {
      invalidar();
      toast.success("Conta a receber excluída (título e parcelas)");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const excluirParcelaMutation = useMutation({
    mutationFn: (parcelaId: number) => contasReceberService.deleteParcela(parcelaId),
    onSuccess: () => {
      invalidar();
      toast.success("Parcela excluída");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const baixarParcelaMutation = useMutation({
    mutationFn: ({ parcelaId, req }: { parcelaId: number; req: BaixarParcelaReceberRequest }) =>
      contasReceberService.baixarParcela(parcelaId, req),
    onSuccess: (r) => {
      invalidar();
      // A mensagem vem do servidor: distingue quitação de recebimento parcial.
      toast.success(r.mensagem);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const baixarParcelasLoteMutation = useMutation({
    mutationFn: (req: BaixarParcelasLoteRequest) => contasReceberService.baixarParcelasLote(req),
    onSuccess: (resultados) => {
      invalidar();
      const ok = resultados.filter((r) => r.parcelaQuitada).length;
      toast.success(`${ok} parcela(s) recebida(s)`);
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const estornarParcelaMutation = useMutation({
    mutationFn: (parcelaId: number) => contasReceberService.estornarParcela(parcelaId),
    onSuccess: () => {
      invalidar();
      toast.success("Estorno da parcela realizado");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  /** Quitação do TÍTULO inteiro — operação distinta da baixa de parcela. */
  const quitarTituloMutation = useMutation({
    mutationFn: ({ id, req }: { id: number; req: BaixarContaReceberRequest }) =>
      contasReceberService.baixar(id, req),
    onSuccess: () => {
      invalidar();
      toast.success("Título quitado por completo");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  const estornarTituloMutation = useMutation({
    mutationFn: (id: number) => contasReceberService.estornar(id),
    onSuccess: () => {
      invalidar();
      toast.success("Estorno do título realizado");
    },
    onError: (error) => toast.error(getApiErrorMessage(error)),
  });

  return {
    parcelas: paginado?.itens ?? [],
    total: paginado?.total ?? 0,
    totalPaginas: paginado?.totalPaginas ?? 0,
    totais: paginado?.totais ?? TOTAIS_ZERADO,
    isLoading,

    criarConta: criarMutation.mutate,
    isCriando: criarMutation.isPending,
    atualizarConta: atualizarMutation.mutate,
    isAtualizando: atualizarMutation.isPending,

    excluirTitulo: excluirTituloMutation.mutate,
    excluirParcela: excluirParcelaMutation.mutate,

    baixarParcela: baixarParcelaMutation.mutate,
    isBaixandoParcela: baixarParcelaMutation.isPending,
    baixarParcelasLote: baixarParcelasLoteMutation.mutate,
    isBaixandoLote: baixarParcelasLoteMutation.isPending,
    estornarParcela: estornarParcelaMutation.mutate,
    isEstornandoParcela: estornarParcelaMutation.isPending,

    quitarTitulo: quitarTituloMutation.mutate,
    isQuitandoTitulo: quitarTituloMutation.isPending,
    estornarTitulo: estornarTituloMutation.mutate,
    isEstornandoTitulo: estornarTituloMutation.isPending,
  };
};

// ── Visão gerencial ────────────────────────────────────────────────────────
// Consultas puras (sem mutação): a gerencial mostra COMO ESTÁ, e as operações
// continuam na tela operacional. Isso é de propósito — misturar as duas foi o
// que produziu "quitei uma parcela e a tela toda mudou de significado".

/** Sintético: quanto cada cliente deve no recorte filtrado. */
export const useGerencialClientesReceber = (filtro: FiltroGerencialReceber = {}) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["contas-receber-gerencial-clientes", filtro],
    queryFn: () => contasReceberService.getGerencialClientes(filtro),
    placeholderData: (prev) => prev,
  });

  return {
    clientes: data?.itens ?? [],
    total: data?.total ?? 0,
    totalPaginas: data?.totalPaginas ?? 0,
    totais: data?.totais ?? TOTAIS_ZERADO,
    isLoading,
    isFetching,
  };
};

/** Analítico: como está cada acordo (total / recebido / saldo). */
export const useGerencialTitulosReceber = (
  filtro: FiltroGerencialReceber = {},
  habilitado = true,
) => {
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["contas-receber-gerencial-titulos", filtro],
    queryFn: () => contasReceberService.getGerencialTitulos(filtro),
    placeholderData: (prev) => prev,
    // Só busca quando o usuário abriu um cliente — evita puxar a base inteira
    // ao entrar na tela.
    enabled: habilitado,
  });

  return {
    titulos: data?.itens ?? [],
    total: data?.total ?? 0,
    totalPaginas: data?.totalPaginas ?? 0,
    totais: data?.totais ?? TOTAIS_ZERADO,
    isLoading,
    isFetching,
  };
};

/** Prazo médio de recebimento (dias entre emissão e recebimento). */
export const usePrazoMedioRecebimento = (
  filtro: FiltroGerencialReceber = {},
  habilitado = true,
) => {
  const { data, isLoading } = useQuery({
    queryKey: ["contas-receber-prazo-medio", filtro],
    queryFn: () => contasReceberService.getPrazoMedio(filtro),
    placeholderData: (prev) => prev,
    enabled: habilitado,
  });

  return {
    prazoMedioDias: data?.prazoMedioDias ?? 0,
    qtdParcelas: data?.qtdParcelas ?? 0,
    valorRecebido: data?.valorRecebido ?? 0,
    porCliente: data?.porCliente ?? [],
    isLoading,
  };
};
